import { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, User, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiGet } from '../../api';
import { useAuth } from '@/context/useAuth';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';

export interface FlowVersion {
    id: string;
    flow_id: string;
    version: string;
    version_number: number;
    description: string | null;
    published_by: string | null;   // user_id of who published
    published_at: string;          // ISO timestamp
    is_active: boolean;
    flow_snapshot?: any;           // only present in single-version fetch
}

interface FlowVersionsPanelProps {
    flowId: string;
    flowName: string;
    onClose: () => void;
    onLoadSnapshot?: (version: FlowVersion) => void;
    mode?: 'view' | 'load'; // 'view' = versions only, 'load' = pick to load
}

export default function FlowVersionsPanel({ flowId, flowName, onClose, onLoadSnapshot, mode = 'view' }: FlowVersionsPanelProps) {
    const { token } = useAuth();
    const { formatDate: fmtDate } = useTimezoneFormatter();
    const [versions, setVersions] = useState<FlowVersion[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        fetchVersions();
    }, [flowId]);

    const fetchVersions = async () => {
        setIsLoading(true);
        try {
            const data = await apiGet(`/flows/${flowId}/versions`, token);
            setVersions(data || []);
        } catch (e) {
            console.error('Failed to fetch versions', e);
            setVersions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoad = (v: FlowVersion) => {
        if (onLoadSnapshot) onLoadSnapshot(v);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

            {/* Panel */}
            <div className="relative z-10 w-80 h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-slate-700 animate-in slide-in-from-right duration-300">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-800">
                    <div>
                        <h2 className="font-semibold text-gray-900 dark:text-slate-100 text-sm">
                            {mode === 'load' ? 'Load Snapshot' : 'Version History'}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-slate-400 truncate max-w-[200px]">{flowName}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
                        <X className="w-4 h-4" />
                    </Button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                    ) : versions.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 dark:text-slate-500">
                            <Tag className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No published versions yet</p>
                            <p className="text-xs mt-1">Publish this flow to create a version</p>
                        </div>
                    ) : (
                        versions.map((v, idx) => (
                            <div
                                key={v.id}
                                onClick={() => mode === 'load' && setSelectedId(v.id)}
                                className={`rounded-xl border p-3 transition-all ${
                                    mode === 'load'
                                        ? 'cursor-pointer hover:border-blue-400 hover:shadow-sm'
                                        : ''
                                } ${
                                    selectedId === v.id
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800/50'
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        {/* Version pill */}
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                            {v.version || `v${v.version_number}`}
                                        </span>
                                        {v.is_active && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                <CheckCircle className="w-2.5 h-2.5" /> Live
                                            </span>
                                        )}
                                        {idx === 0 && !v.is_active && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
                                                Latest
                                            </span>
                                        )}
                                    </div>
                                    {mode === 'load' && selectedId === v.id && (
                                        <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <div className="w-2 h-2 rounded-full bg-white" />
                                        </div>
                                    )}
                                </div>

                                {v.description && (
                                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1.5 line-clamp-2">
                                        {v.description}
                                    </p>
                                )}

                                <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400 dark:text-slate-500">
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {fmtDate(v.published_at)}
                                    </span>
                                    {v.published_by && (
                                        <span className="flex items-center gap-1">
                                            <User className="w-3 h-3" />
                                            {v.published_by.substring(0, 8)}…
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer — only in load mode */}
                {mode === 'load' && (
                    <div className="border-t border-gray-100 dark:border-slate-800 px-3 py-3">
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5 rounded-lg">
                            ⚠ Loading a snapshot replaces the current editor canvas. Edit and publish to save changes.
                        </p>
                        <Button
                            className="w-full"
                            disabled={!selectedId}
                            onClick={() => {
                                const v = versions.find(x => x.id === selectedId);
                                if (v) handleLoad(v);
                            }}
                        >
                            Load into Editor
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
